import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { MainNav } from '../components/MainNav';
import { useAuthStore } from '@/store/auth';

export function EmployeeSignup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationCode, setOrganizationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuthStore();

  // Generate username preview from name: first initial + last name, lowercase
  const getUsername = () => {
    if (!firstName || !lastName) return '';
    return (firstName.charAt(0) + lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!firstName.trim()) {
      setError('FIRST NAME REQUIRED');
      return;
    }
    if (!lastName.trim()) {
      setError('LAST NAME REQUIRED');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('VALID EMAIL REQUIRED');
      return;
    }
    if (!organizationName.trim()) {
      setError('ORGANIZATION NAME REQUIRED');
      return;
    }
    if (!organizationCode.trim()) {
      setError('ORGANIZATION CODE REQUIRED');
      return;
    }
    if (!password) {
      setError('PASSWORD REQUIRED');
      return;
    }
    if (password.length < 6) {
      setError('PASSWORD MUST BE AT LEAST 6 CHARACTERS');
      return;
    }
    if (password !== confirmPassword) {
      setError('PASSWORDS DO NOT MATCH');
      return;
    }

    const username = getUsername();
    if (username.length < 2) {
      setError('INVALID NAME FORMAT');
      return;
    }

    setLoading(true);

    try {
      // Call backend API for employee signup
      const response = await apiClient.post('/auth/employee/signup', {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        password: password,
        organization_name: organizationName.trim(),
        organization_code: organizationCode.trim().toUpperCase(),
      });

      const data = response.data;

      // Log the user in with the returned tokens
      login(
        {
          id: data.user.id,
          email: data.user.email,
          full_name: data.user.name,
          role: data.user.role,
          client_id: data.user.client_id,
          is_active: data.user.is_active ?? true,
          last_login: data.user.last_login ?? null,
        },
        data.access_token,
        data.refresh_token
      );

      // Give zustand persist time to write to localStorage
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate to dashboard
      window.location.href = '/dashboard';
      return;
    } catch (err: any) {
      const message = err.response?.data?.detail || 'SIGNUP FAILED';
      setError(typeof message === 'string' ? message.toUpperCase() : 'SIGNUP FAILED');
      setLoading(false);
    }
  };

  const previewUsername = getUsername();

  return (
    <div className="min-h-screen bg-black">
      <MainNav />

      <div className="max-w-sm mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h1 className="text-xl font-bold text-white tracking-widest mb-2">
            EMPLOYEE SIGNUP
          </h1>
          <p className="text-[10px] text-white/50 tracking-wider">
            JOIN YOUR ORGANIZATION
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="border border-red-500/50 bg-red-500/10 p-3 text-center">
              <p className="text-xs text-red-400 tracking-wider">{error}</p>
            </div>
          )}

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                FIRST NAME
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
                placeholder="John"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                LAST NAME
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
                placeholder="Smith"
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-[10px] text-white/50 tracking-widest mb-2">
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
              placeholder="you@company.com"
            />
            <p className="text-[10px] text-white/30 tracking-wider mt-1">
              Used for login and notifications
            </p>
          </div>

          {/* Organization Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                ORGANIZATION NAME
              </label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
                placeholder="Your company name"
              />
            </div>

            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                ORGANIZATION CODE
              </label>
              <input
                type="text"
                value={organizationCode}
                onChange={(e) => setOrganizationCode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide font-mono"
                placeholder="Enter code"
                maxLength={10}
              />
              <p className="text-[10px] text-white/30 tracking-wider mt-1">
                Provided by your organization
              </p>
            </div>
          </div>

          {/* Password Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
                placeholder="Min 6 characters"
              />
            </div>

            <div>
              <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                CONFIRM PASSWORD
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/20 text-white text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
                placeholder="Confirm password"
              />
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading || !firstName || !lastName || !email || !organizationName || !organizationCode}
              className="w-full py-3 border border-cyan-500 bg-cyan-500 text-black text-xs font-bold tracking-widest hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'CREATING ACCOUNT...' : 'ACCESS ARGOS'}
            </button>
          </div>
        </form>

        {/* Links */}
        <div className="mt-8 text-center space-y-3">
          <p className="text-[10px] text-white/30 tracking-wider">
            ALREADY HAVE AN ACCOUNT?{' '}
            <Link to="/employee/login" className="text-cyan-400 hover:text-cyan-300">
              LOGIN
            </Link>
          </p>
          <p className="text-[10px] text-white/30 tracking-wider">
            <Link to="/employee" className="text-white/50 hover:text-white">
              BACK TO EMPLOYEE PORTAL
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
